import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';
import {
  NetscopeApiService,
  Success,
  Response
} from "@app/services/netscope-api.service";
import {EChartsOption} from "echarts";
import {NetscopeUtils} from "@app/netscope/netscope-utils";
import {DateTime} from "luxon";
import { NetscopeUiService } from '@app/services/netscope-ui.service';

type DailyHeatmapData = {
  value: [string, number], // date + actual value
  meta: {
    timestamp: number
  }
}

@Component({
    standalone: false,
  selector: 'app-heatmap-highcharts-heatmap',
  templateUrl: './heatmap-highcharts-heatmap.component.html',
  styleUrls: ['./heatmap-highcharts-heatmap.component.css']
})
export class HeatmapHighchartsHeatmapComponent implements OnInit {

  @Input()
  temporalResolution: "daily" | "hourly" = "daily";
  @Input()
  selectedResources1;
  @Input()
  selectedResources2;
  // @Input()
  // bidirectional: boolean = true;
  @Input()
  directionMode: string;
  @Input()
  onClick;

  @Input()
  updateEmitter: EventEmitter<any>;
  @Input()
  clickOnTimeSlotEmitter: EventEmitter<any>;
  @Input()
  loadingStateEmitter: EventEmitter<any>;

  @Input()
  maxHeight = 400;
  @Input()
  legendMaxHeight = 280;
  @Input()
  doubleClickSelectDay = false;
  @Input()
  selectLastDay = false;
  @Input()
  selectedTime = undefined;
  @Input()
  selectedTimePeriod = undefined;

  @Input()
  tickFunction = undefined

  heatmapOptions: EChartsOption;


  selectionPosition = {
    plotSelection: {
      type: "none", // can be "none", "hourly", "daily",
      x: -1,
      y: -1
    },
    rectanglePositions: {
      x: 0,
      y: 0,
      height: 0,
      width: 0
    }
  }

  // Save heatmap config (useful for pre-selecting the heatmap on a specific time)
  heatmapFlowsData;

  colorAxisScaleType = "linear";

  isLoading = false;

  isLightTheme = true;

  constructor(private netscopeService: NetscopeApiService, private netscopeUiService: NetscopeUiService) {
  }

  ngOnInit(): void {
    this.updateEmitter.subscribe((e) => {

      if (e.bidirectional !== undefined) {
        this.directionMode = e.directionMode;
      }

      if (e.temporalResolution !== undefined) {
        this.temporalResolution = e.temporalResolution;
      }

      if (e.reloadData) {
        this.displayHeatmap();
      } else {
        this.reloadUi();
      }

    })
    this.displayHeatmap();

    this.netscopeUiService.themeChangeEmitter.subscribe(isLightTheme => {
      this.isLightTheme = isLightTheme;
      this.reloadUi();
    })
  }

  ngOnDestroy = () => {
  }

  onChartClick = (event) => {
    if (this.temporalResolution == "hourly") {
      let [x, y, value] = event.data;
      let correspondingTimestamp = this.heatmapFlowsData.x[x] + y * 3600;
      console.log(correspondingTimestamp)
      this.clickOnTimeSlotEmitter.emit([correspondingTimestamp, "", "hourly"]);
    } else {
      let correspondingTimestamp = event.data.meta.timestamp;
      console.log(correspondingTimestamp)
      this.clickOnTimeSlotEmitter.emit([correspondingTimestamp, "", "daily"]);
    }
  }

  updateColorAxisOptions = () => {
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    if (this.selectionPosition.plotSelection.type !== "none") {
    }
  }

  displayHeatmap = () => {
    this.isLoading = true;
    if (this.loadingStateEmitter !== undefined) {
      this.loadingStateEmitter.emit(true);
    }

    let srcUuid = undefined;
    let dstUuid = undefined;

    for (let resource of this.selectedResources1) {
      srcUuid = resource.uuid;
    }

    for (let resource of this.selectedResources2) {
      dstUuid = resource.uuid;
    }

    if (srcUuid === undefined) {
      return;
    }

    const heatmapFlowsDataObservable = this.netscopeService.getHeatmapFlowsData(srcUuid, dstUuid, this.directionMode);

    heatmapFlowsDataObservable.subscribe((heatmapFlowsDataResponse: Response<any>) => {

      let heatmapFlowsData;
      if (heatmapFlowsDataResponse instanceof Success) {
        heatmapFlowsData = heatmapFlowsDataResponse.element;
      }

      this.heatmapFlowsData = heatmapFlowsData;
      this.reloadUi();
    });
  }

  reloadUi = () => {

    let style = getComputedStyle(document.body);
    let textColor = this.isLightTheme ? style.getPropertyValue('--color-text-charts-light') : style.getPropertyValue('--color-text-charts-dark');

    let hourlySerieData = this.heatmapFlowsData.alternate_data;
    let dailySerieDataTmpMap: Map<number, number> = new Map<number, number>();
    for (let [x, y, z] of hourlySerieData) {
      if (!dailySerieDataTmpMap.has(x)) {
        dailySerieDataTmpMap.set(x, 0);
      }
      dailySerieDataTmpMap.set(x, dailySerieDataTmpMap.get(x) + z);
    }
    let dailySerieDataTmpMapRows = Array.from(dailySerieDataTmpMap.entries()).sort((a, b) => a[0] - b[0]);
    let minDayDate = undefined;
    let maxDayDate = undefined;
    let dailySerieData: DailyHeatmapData[] = [];
    for (let t2 of dailySerieDataTmpMapRows) {
      let [x, z] = t2;
      let datetime = DateTime.fromSeconds(this.heatmapFlowsData.x[x]);
      let datetime_str = datetime.toFormat('yyyy-MM-dd');

      let timestamp = this.heatmapFlowsData.x[x];

      let newDailyHeatmapData: DailyHeatmapData = {
        value: [datetime_str, z],
        meta: {
          timestamp: timestamp
        }
      }

      dailySerieData.push(newDailyHeatmapData);

      if (minDayDate === undefined) {
        minDayDate = datetime_str;
      }
      maxDayDate = datetime_str;
    }

    if (this.temporalResolution == "hourly") {
      let allValues = this.heatmapFlowsData.alternate_data.map(([x, y, z]) => z);
      let minValue = NetscopeUtils.getMin(allValues);
      let maxValue = NetscopeUtils.getMax(allValues);
      this.heatmapOptions = {
        tooltip: {
          position: 'top',
          formatter: function (p) {
            let valueAsFloat = Number.parseFloat(p.name);
            let datetime = DateTime.fromSeconds(valueAsFloat);
            let hourFormatted = `${p.data[1]}:00`;
            let dayFormatted = datetime.toLocaleString();
            let bytesFormatted = NetscopeUtils.formatBytes(p.data[2]);
            return `${dayFormatted} ${hourFormatted} : ${bytesFormatted}`;
          }
        },
        grid: {
          left: '10px',
          right: '110px',
          top: '10%',
          bottom: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.heatmapFlowsData.x,
          // name: 'Date',
          axisLabel: {
            formatter: (value, index) => {
              let valueAsFloat = Number.parseFloat(value);
              let datetime = DateTime.fromSeconds(valueAsFloat);
              return datetime.toLocaleString();
            },
            color: textColor,
/*             textBorderColor: 'var(--highcharts-neutral-color-10)',
            textBorderWidth: 1 */
          },
        },
        yAxis: {
          type: 'category',
          inverse: true,
          data: this.heatmapFlowsData.y,
          // name: 'Hour',
          axisLabel: {
            formatter: (value, index) => {
              return `${value}:00`
            }
          }
        },
        visualMap: {
          min: minValue,
          max: maxValue,
          calculable: true,
          orient: 'vertical',
          left: 'right',
          // bottom: '-5%',
          top: 'center',
          formatter: function (value) {
            return NetscopeUtils.formatBytes(value);
          }
        },
        series: [{
          name: 'Exchanged bytes',
          type: 'heatmap',
          data: this.heatmapFlowsData.alternate_data,
          label: {
            show: false
          },
          emphasis: {
            itemStyle: {
              borderColor: '#333',
              borderWidth: 1
            }
          }
        }]
      };

    } else {
      let allValues = dailySerieData.map((data) => data.value[1]);
      let minValue = NetscopeUtils.getMin(allValues);
      let maxValue = NetscopeUtils.getMax(allValues);

      this.heatmapOptions = {
        title: {
          top: 30,
          left: 'center',
          // text: 'Daily Step Count'
        },
        tooltip: {
          position: 'top',
          formatter: function (p) {
            let datetime = DateTime.fromSeconds(p.data.meta.timestamp);
            let dayFormatted = datetime.toLocaleString();
            let bytesFormatted = NetscopeUtils.formatBytes(p.data.value[1]);
            return `${dayFormatted} : ${bytesFormatted}`;
          }
        },
        visualMap: {
          min: minValue,
          max: maxValue,
          calculable: true,
          orient: 'vertical',
          left: 'right',
          top: 'center',
          formatter: function (value) {
            return NetscopeUtils.formatBytes(value);
          }
        },
        calendar: {
          left: '10px',
          right: '110px',
          top: '10%',
          bottom: '10%',
          cellSize: ['auto', 13],
          range: [minDayDate, maxDayDate],
          itemStyle: {
            borderWidth: 0.5,
            color: 'transparent',
          },
          yearLabel: {
            show: true,
            margin: 40,
            fontSize: 18,
            //color: '#888'
            color: textColor
          },
          monthLabel: {
            color: textColor
          },
          dayLabel: {
            firstDay: 1,
            color: textColor
          }
        },
        series: {
          type: 'heatmap',
          coordinateSystem: 'calendar',
          data: dailySerieData
        }
      };
    }

    this.isLoading = false;
    if (this.loadingStateEmitter !== undefined) {
      this.loadingStateEmitter.emit(false);
    }

  }
}

